import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Dialog, Snackbar } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchVoiceCallSession,
  fetchCandidateProfile,
  fetchVoiceAssistants,
  fetchAssessmentInviteDetails,
  fetchVideoCallDetails,
  clearCandidateSessionData,
  getPreScreeningReport,
  getVoiceSessionStatus,
  updateCandidateDetails,
  createShareLink,
  sendShareEmail,
} from "../../Redux/ActionCreators/candidateAction";
import {
  assignedStage,
  fetchAvailableSlots,
  fetchJobById,
} from "../../Redux/ActionCreators/jobsAction";
import { getHiringStatuses } from "../../Redux/ActionCreators/settingAction";
import "./candidateProfileV2.scss";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import TimeZone from "../../utils/all_timezones.json";
import ApplyStageModal from "../../utils/applyStageModal";
import Lightbox from "../../utils/lightbox";
import CreatableSelect from "react-select/creatable";
import {
  fetchFlows,
  initiateOnboarding,
  uploadDocument,
  getRecruiterRunView,
  requestRunChanges,
  completeRunReview,
} from "../../Redux/ActionCreators/onboardingAction";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";
import {
  refreshRecruiterConversation,
  resetChat,
  fetchCandidateComments,
  postCandidateComment,
  patchCandidateComment,
  deleteCandidateComment,
  fetchCandidateFiles,
  fetchCandidateActivity,
} from "../../Redux/ActionCreators/chatAction";
import PreScreeningStage from "../../utils/PreScreeningStage";
import VoiceInterviewStage from "../../utils/VoiceInterviewStage";
import AssessmentStage from "../../utils/AssessmentStage";
import VideoInterviewStage from "../../utils/VideoInterviewStage";
import RecruiterChat from "../Chat/RecruiterChat";
import ScoreCircle from "../../utils/ScoreCircle";
import { getStatusVisual } from "../../utils/candidatesV2Constants";

// ── Constants ─────────────────────────────────────────────────────────

const StageIds = {
  Assessment: "293e9b95-4610-48f2-ae93-c7264cae7d6a",
  "Video Call": "ed658e02-1549-4d9e-a735-1f1c1e385f46",
  "Voice Call": "fd40e3d2-38a9-4cef-937a-93ef6a42d045",
  "Pre Screening": "3dab71cf-c680-4983-a109-1cff0953999b",
};

const ALLOWED_REASSIGN_STATUSES = ["not scheduled", "failed", "expired", "revoked"];

const STATUS_COLORS = {
  COMPLETED: "#22c55e",
  EXPIRED: "#ef4444",
  ASSIGNED: "#0048b7",
  SCHEDULED: "#f59e0b",
  ONGOING: "#0048b7",
  UNASSIGNED: "#8a9199",
};

const STAGE_CARDS = [
  { label: "Pre-Screening", stageKey: "Pre-Screening", typeIndex: 0, masterStageId: StageIds["Pre Screening"] },
  { label: "Telephonic Interview", stageKey: "Voice Interview", typeIndex: 1, masterStageId: StageIds["Voice Call"] },
  { label: "Assessments", stageKey: "Assessments", typeIndex: 2, masterStageId: StageIds["Assessment"] },
  { label: "Video Interview", stageKey: "Video Interview", typeIndex: 3, masterStageId: StageIds["Video Call"] },
];

const FIT_LABEL_CONFIG = {
  EXCELLENT_FIT: { title: "Excellent Fit", color: "#22c55e" },
  GOOD_FIT: { title: "Good Fit", color: "#F59E0B" },
  NOT_FIT: { title: "Not a Fit", color: "#ef4444" },
};

const normalizeStatus = (s) => (s || "").trim().toLowerCase();

const formatLastActive = (lastActiveAt, isOnline) => {
  if (isOnline) return 'Active now';
  if (!lastActiveAt) return 'Offline';
  const now = new Date();
  const lastActive = new Date(lastActiveAt);
  const diffMin = Math.floor((now - lastActive) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin <= 10) return `${diffMin} min ago`;
  const day = lastActive.getDate();
  const month = lastActive.toLocaleString('en-US', { month: 'short' });
  const year = lastActive.getFullYear();
  const time = lastActive.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${day} ${month} ${year}, ${time}`;
};

// ── File type icon (mirrors DocumentCenter) ───────────────────────────

const resolveFileType = (name = '', mimeType = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || mimeType.includes('pdf'))
    return { color: '#991b1b', bg: '#fee2e2', label: 'PDF' };
  if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return { color: '#15803d', bg: '#dcfce7', label: 'XLS' };
  if (['doc', 'docx'].includes(ext) || mimeType.includes('word') || mimeType.includes('doc'))
    return { color: '#1e40af', bg: '#dbeafe', label: 'DOC' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image'))
    return { color: '#92400e', bg: '#fef3c7', label: 'IMG' };
  if (['txt', 'md', 'log'].includes(ext) || mimeType.includes('text') || mimeType.includes('plain'))
    return { color: '#374151', bg: '#f3f4f6', label: 'TXT' };
  if (['ppt', 'pptx'].includes(ext))
    return { color: '#c2410c', bg: '#ffedd5', label: 'PPT' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
    return { color: '#6d28d9', bg: '#ede9fe', label: 'ZIP' };
  return { color: '#6b7280', bg: '#f3f4f6', label: ext.toUpperCase().slice(0, 4) || 'FILE' };
};

const FileTypeIcon = ({ name, mimeType = '' }) => {
  const { color, bg, label } = resolveFileType(name, mimeType);
  return (
    <svg width="28" height="33" viewBox="0 0 36 42" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path d="M4 0h20l12 12v26a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill={bg} />
      <path d="M24 0l12 12H28a4 4 0 0 1-4-4V0z" fill={color} fillOpacity="0.35" />
      <text x="18" y="30" textAnchor="middle" fill={color} fontSize="8.5" fontWeight="800" fontFamily="Arial, sans-serif" letterSpacing="0.5">{label}</text>
    </svg>
  );
};

// ── Icons ─────────────────────────────────────────────────────────────

const IconMsg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconActivity = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const IconNotes = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconFiles = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconPreScreening = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

const IconSparkle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e91e8c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 2l.9 2.6L22 5.5l-2.1.9L19 9l-.9-2.6L16 5.5l2.1-.9z" />
    <path d="M5 17l.7 1.8L7.5 19.5l-1.8.7L5 22l-.7-1.8L2.5 19.5l1.8-.7z" />
  </svg>
);

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.71-.71a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IconAssessment = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const IconVideo = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const IconMail = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconPhoneSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.71-.71a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IconLocation = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconBack = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconShare = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const IconChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const IconExternalLink = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const STAGE_ICONS = {
  0: <IconPreScreening />,
  1: <IconPhone />,
  2: <IconAssessment />,
  3: <IconVideo />,
};

const MATCH_LABEL_CONFIG = {
  "Excellent match": { color: "#4CC948", arrow: "up" },
  "Good match":      { color: "#F59E0B", arrow: "up" },
  "Fair match":      { color: "#F59E0B", arrow: "up" },
  "Weak match":      { color: "#F75891", arrow: "down" },
  "Poor match":      { color: "#ef4444", arrow: "down" },
};

const getScoreBadgeStyle = (score) => {
  if (score >= 75) return { color: "#4CC948", background: "#f0fff4" };
  if (score >= 50) return { color: "#F59E0B", background: "#fffbeb" };
  return { color: "#ef4444", background: "#fff5f5" };
};

// ── Score helpers ─────────────────────────────────────────────────────


function getStatusLabel(status) {
  switch (status) {
    case "COMPLETED": return "Done";
    case "ASSIGNED": return "Assigned";
    case "SCHEDULED": return "Scheduled";
    case "ONGOING": return "In Progress";
    case "EXPIRED": return "Expired";
    case "FAILED": return "Failed";
    case "NOT COMPLETED": return "Not Completed";
    case "UNASSIGNED": return "Not Assigned";
    default: return status || "Not Assigned";
  }
}

// ── Component ─────────────────────────────────────────────────────────

const CandidateProfileV2 = (props) => {
  const { can } = useAppPermission();
  const canInitiateOnboarding = can(P.ONBOARDING_FLOW_CREATE);
  const canEditOnboarding = can(P.ONBOARDING_FLOW_EDIT);
  const canReadOnboarding = can(P.ONBOARDING_FLOW_READ);
  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [testType, setTestType] = useState(0);
  const [stageLoading, setStageLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [loadingCall, setLoadingCall] = useState(false);
  const [assessmentStatus] = useState("idle");
  const [showInlineReport, setShowInlineReport] = useState(false);
  const [inlineReportId, setInlineReportId] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [videoAgentType, setVideoAgentType] = useState("");
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [timezone, setTimezone] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [initiatingVideoCall] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandSummary, setExpandSummary] = useState(false);
  const [expandRecommendation, setExpandRecommendation] = useState(false);
  const [expandFitRationale, setExpandFitRationale] = useState(false);
  const [showPhoneCheckModal, setShowPhoneCheckModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [preselectStage, setPreselectStage] = useState(null);
  const [assignProgress, setAssignProgress] = useState({ current: 0, total: 0 });
  const [showAllCheatingImages, setShowAllCheatingImages] = useState(false);
  const [openProctorLightbox, setOpenProctorLightbox] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedProctorImage, setSelectedProctorImage] = useState("");
  const [selectedProctorIndex, setSelectedProctorIndex] = useState(null);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupData, setPopupData] = useState([]);
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareEmails, setShareEmails] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [isEmailHovered, setIsEmailHovered] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [snackMessage, setSnackMessage] = useState("");
  // Onboarding
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [onboardingFlows, setOnboardingFlows] = useState([]);
  const [onboardingForm, setOnboardingForm] = useState({ flowId: "", joiningDate: "" });
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingAdditionalDocs, setOnboardingAdditionalDocs] = useState([]);
  const [onboardingFlowsLoading, setOnboardingFlowsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [onboardingViewModal, setOnboardingViewModal] = useState({ open: false, runId: null, data: null, loading: false });
  const [onboardingCommentModal, setOnboardingCommentModal] = useState({ open: false, runId: null, value: "" });
  const [expandedOnboardingForms, setExpandedOnboardingForms] = useState({});
  // Chat panel
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [chatActiveTab, setChatActiveTab] = useState("messages");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusChangeModal, setStatusChangeModal] = useState({ open: false, status: null, comment: "" });
  // Notes tab
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [notePendingFiles, setNotePendingFiles] = useState([]);
  const [notePosting, setNotePosting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteBody, setEditNoteBody] = useState("");
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  // Files tab
  const [candidateFiles, setCandidateFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  // Activity tab
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // ── Refs ─────────────────────────────────────────────────────────
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const additionalDocInputRef = useRef(null);
  const noteFileInputRef = useRef(null);
  const flowSearchDebounceRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const skillsMoreRef = useRef(null);
  const [skillsTooltipPos, setSkillsTooltipPos] = useState(null);

  let jobId = params.jobId;
  let candidateId = params.candidateId;
  if (!jobId && props.jobId) jobId = props.jobId;
  if (!candidateId && props.candidateId) candidateId = props.candidateId;

  const isSharedReport = props.isSharedReport === true;
  const hideSensitive = props.hideSensitive === true;

  // ── Selectors ────────────────────────────────────────────────────
  const candidate = useSelector((s) => s.candidates.selectedCandidate);
  const videoSession = useSelector((s) => s.candidates.videoSessionData);
  const voiceSession = useSelector((s) => {
    const sess = s.candidates.voiceSessionData;
    return sess?.job_candidate_id === candidate?.id ? sess : null;
  });
  const assessmentInviteDetails = useSelector((s) => s.candidates.assessmentInviteDetails);
  const latestJob = useSelector((s) => s.jobs.latestJob);
  const availableSlots = useSelector((s) => s.jobs.availableSlots);
  const chatConversation = useSelector((s) => s.chat.conversation);
  const preQualifyingReport = useSelector((s) => s.candidates.preQualifyingReport);
  const hiringStatusesRaw = useSelector((s) => s.setting.hiringStatuses || []);
  const hiringStatuses = useMemo(
    () => hiringStatusesRaw
      .filter((st) => st.is_active !== false)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [hiringStatusesRaw]
  );

  const fromTab = location.state?.fromTab || "prescreening";
  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const score = candidate?.match_score || 0;
  const selectedAssistant = assistants.find((a) => a.id === selectedAssistantId);

  // Proctoring
  let proctoring = videoSession?.result?.proctoring_analysis;
  let cheatingFrames = [];
  if (proctoring?.samples?.length > 0) {
    cheatingFrames = proctoring.samples.filter((s) => s?.llm_eval?.cheating_detected === true);
  }

  // ── Effects ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!jobId || !candidateId) { setLoading(false); return; }
      setLoading(true);
      dispatch(clearCandidateSessionData());
      dispatch(resetChat());
      await dispatch(fetchJobById(jobId));
      await dispatch(fetchCandidateProfile(jobId, candidateId));
      dispatch(refreshRecruiterConversation(candidateId));
      const res = await dispatch(createShareLink({ job_id: jobId, job_candidate_id: candidateId }));
      if (res?.success) setShareData(res.data);
      setLoading(false);
    };
    fetchData();
  }, [jobId, candidateId, dispatch]);

  useEffect(() => {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matched = TimeZone.find((tz) => tz.value === detected);
    if (matched) setTimezone(matched);
  }, []);

  useEffect(() => {
    if (isSharedReport) return;
    dispatch(getHiringStatuses());
  }, [isSharedReport, dispatch]);


  useEffect(() => {
    if (!candidate?.id) return;
    const tabTypeMap = { prescreening: 0, voice: 1, assessment: 2, video: 3 };
    const tabIndex = tabTypeMap[fromTab] ?? 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    changeType(tabIndex);
  }, [fromTab, candidate?.id]); // changeType is stable within render; intentionally omitted

  useEffect(() => {
    if (!jobId || !scheduleDate || !timezone) return;
    dispatch(fetchAvailableSlots(jobId, moment(scheduleDate).format("YYYY-MM-DD"), timezone.value));
  }, [scheduleDate, timezone, dispatch, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Computed ─────────────────────────────────────────────────────
  const videoCallJobStage = latestJob?.hiring_stages?.find(
    (s) => s.hiring_stage_id === StageIds["Video Call"]
  );
  const isVideoSettingsMissing =
    modalType === "video" &&
    videoAgentType === "hybrid" &&
    videoCallJobStage?.data?.video_interview_settings?.enable_ai_bot !== true;

  const voiceCallJobStage = latestJob?.hiring_stages?.find(
    (s) => s.hiring_stage_id === StageIds["Voice Call"]
  );
  const isDynamicVoiceCallMode =
    voiceCallJobStage?.data?.telephonic_call_mode === "DYNAMIC_VOICE_CALL";
  const isDynamicVoiceAssistantEnabled =
    voiceCallJobStage?.data?.dynamic_voice_assistant?.enabled === true;
  const voiceFitLabel = voiceSession?.result?.analysis?.fit_label || null;

  const filterStageId = latestJob?.hiring_stages?.find(
    (el) =>
      el.hiring_stage_id ===
      (testType === 0 ? StageIds["Pre Screening"]
        : testType === 1 ? StageIds["Voice Call"]
        : testType === 2 ? StageIds["Assessment"]
        : StageIds["Video Call"])
  )?.id;

  const stagePermissionMap = useMemo(() => {
    if (!candidate?.hiring_stages?.length) return {};
    const grouped = {};
    candidate.hiring_stages.forEach((stage) => {
      const mid = stage.hiring_stage_id;
      if (!mid) return;
      if (!grouped[mid]) grouped[mid] = [];
      grouped[mid].push(stage);
    });
    const result = {};
    Object.entries(grouped).forEach(([mid, records]) => {
      const latest = records.reduce((a, b) =>
        new Date(b.created_at) > new Date(a.created_at) ? b : a
      );
      const latestStatus = normalizeStatus(latest?.status?.name);
      const canAssign = !latestStatus || ALLOWED_REASSIGN_STATUSES.includes(latestStatus);
      result[mid] = {
        latestStatus, canAssign,
        message: canAssign ? null : `This stage was already been ${latest.status.name}, you cannot assign again`,
      };
    });
    return result;
  }, [candidate]);

  const getCurrentStageObject = () => {
    const stageKeyMap = [StageIds["Pre Screening"], StageIds["Voice Call"], StageIds["Assessment"], StageIds["Video Call"]];
    const currentStageId = stageKeyMap[testType];
    if (!currentStageId || !candidate?.hiring_stages?.length) return null;
    const stages = candidate.hiring_stages.filter((s) => s.hiring_stage_id === currentStageId);
    if (!stages.length) return null;
    return stages.reduce((latest, curr) => {
      const ct = new Date(curr.updated_at || curr.created_at).getTime();
      const lt = new Date(latest.updated_at || latest.created_at).getTime();
      if (ct !== lt) return ct > lt ? curr : latest;
      return curr.id > latest.id ? curr : latest;
    });
  };

  const currentStage = getCurrentStageObject();
  const currentStatus = currentStage?.status?.name?.toUpperCase() || null;

  const getStageInfo = (stageKey) => {
    if (!candidate?.hiring_stages) return { status: "UNASSIGNED", score: null };
    const stageMap = {
      "Pre-Screening": StageIds["Pre Screening"],
      "Voice Interview": StageIds["Voice Call"],
      Assessments: StageIds["Assessment"],
      "Video Interview": StageIds["Video Call"],
    };
    const stageId = stageMap[stageKey];
    const stages = candidate.hiring_stages.filter((s) => s.hiring_stage_id === stageId);
    if (!stages.length) return { status: "UNASSIGNED", score: null };
    const latestStage = stages.reduce((l, c) =>
      new Date(c.created_at) > new Date(l.created_at) ? c : l
    );
    const status = latestStage?.status?.name?.toUpperCase() || "UNASSIGNED";
    return { status, score: status === "COMPLETED" ? latestStage?.score : null };
  };

  const hasPreScreeningStage = latestJob?.hiring_stages?.some(
    (s) => s.hiring_stage_id === StageIds["Pre Screening"]
  );

  const visibleStageCards = STAGE_CARDS.filter((card) => {
    if (card.typeIndex === 0 && !hasPreScreeningStage) return false;
    return true;
  });

  // ── Handlers ─────────────────────────────────────────────────────
  const changeType = async (typeIndex) => {
    setTestType(typeIndex);
    setTabLoading(true);
    try {
      if (typeIndex === 0) {
        const preStages = candidate?.hiring_stages?.filter(
          (s) => s.hiring_stage_id === StageIds["Pre Screening"]
        );
        const pre = preStages?.reduce((l, c) =>
          new Date(c.created_at) > new Date(l.created_at) ? c : l, preStages[0]
        ) || null;
        if (pre?.status?.name?.trim().toLowerCase() === "completed") {
          dispatch({ type: "CLEAR_PREQUALIFYING_REPORT" });
          await dispatch(getPreScreeningReport(candidateId));
        }
      }
      if (typeIndex === 1 && candidate?.voice_call_id && !voiceSession)
        await dispatch(fetchVoiceCallSession(candidate.voice_call_id));
      if (typeIndex === 2 && candidate?.invite_id && !assessmentInviteDetails)
        await dispatch(fetchAssessmentInviteDetails(candidate.invite_id));
      if (typeIndex === 3 && candidate?.video_call_id && !videoSession)
        await dispatch(fetchVideoCallDetails(candidate.video_call_id));
    } catch (e) {
      console.error("changeType error:", e);
    } finally {
      setTabLoading(false);
    }
  };

  const showSnack = (msg, sev = "error") => {
    setSnackMessage(msg);
    setSnackSeverity(sev);
    setSnackOpen(true);
  };

  const snackClosed = (e, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const formatDateTime = (d) => {
    const date = new Date(d);
    return date.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    }).replace(",", "");
  };

  const formatDateOnly = (d) =>
    new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  const formatExpiry = (d) => moment(d).format("MMMM-DD-YYYY");

  const renderActivityChangeValue = (value_type, display) => {
    if (display == null || display === "") return <span style={{ color: "#94a3b8", fontStyle: "italic" }}>—</span>;
    switch (value_type) {
      case "status":
        return (
          <span style={{
            display: "inline-block", padding: "1px 7px", borderRadius: 10,
            background: "#e8f0fe", color: "#1c2b4a", fontSize: 10, fontWeight: 600, letterSpacing: 0.2,
          }}>
            {display}
          </span>
        );
      case "boolean":
        return (
          <span style={{
            display: "inline-block", padding: "1px 7px", borderRadius: 10,
            background: display === true || display === "true" ? "#dcfce7" : "#fee2e2",
            color: display === true || display === "true" ? "#166534" : "#991b1b",
            fontSize: 10, fontWeight: 600,
          }}>
            {display === true || display === "true" ? "Yes" : "No"}
          </span>
        );
      case "number":
        return <span style={{ fontWeight: 600, color: "#1c2b4a", fontSize: 11 }}>{display}</span>;
      case "url":
        return (
          <a href={display} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline", wordBreak: "break-all" }}>
            {display.length > 40 ? display.slice(0, 37) + "…" : display}
          </a>
        );
      case "uuid":
      case "string":
      default:
        return <span style={{ fontSize: 11, color: "#1e293b" }}>{String(display)}</span>;
    }
  };

  const handleVoiceCall = async () => {
    setLoadingCall(true);
    const result = await dispatch(fetchVoiceAssistants());
    setLoadingCall(false);
    if (result.success) {
      setAssistants(result.data);
      setShowAssistantModal(true);
    }
  };

  const handleVoiceRefresh = async () => {
    if (!currentStage?.created_at) return;
    const diffMinutes = Math.floor((new Date() - new Date(currentStage.created_at)) / 60000);
    if (diffMinutes < 15) {
      showSnack(
        `Voice call triggered at ${formatDateTime(currentStage.created_at)}. Please wait ${15 - diffMinutes} more minute(s).`,
        "info"
      );
    } else {
      try {
        setRefreshing(true);
        setTabLoading(true);
        await dispatch(getVoiceSessionStatus(candidate.voice_call_id));
        await dispatch(fetchCandidateProfile(jobId, candidateId));
        showSnack("Candidate profile updated successfully.", "success");
      } catch {
        showSnack("Failed to refresh profile. Please try again.");
      } finally {
        setRefreshing(false);
        setTabLoading(false);
      }
    }
  };

  const handleScheduleVoice = () => {
    setShowVideoModal(true);
    setModalType("voice");
    dispatch(fetchAvailableSlots(jobId, moment(scheduleDate).format("YYYY-MM-DD"), detectedTz));
  };

  const handleScoreClick = (type) => {
    if (!voiceSession?.result?.analysis?.structured_analysis) return;
    const structured = voiceSession.result.analysis.structured_analysis;
    const weights = structured.weights_used || {};
    let data = [], title = "", sc = 0, max = 0;
    if (type === "technical") {
      title = "Technical Weighted Score";
      data = structured.technical_evaluation || [];
      sc = structured.technical_weighted_score || 0;
      max = weights.technical || 60;
    } else if (type === "cultural") {
      title = "Cultural Fit Weighted Score";
      data = structured.cultural_fit_evaluation || [];
      sc = structured.cultural_fit_weighted_score || 0;
      max = weights.cultural_fit || 15;
    } else if (type === "final") {
      title = "Final Weighted Score";
      data = [...(structured.technical_evaluation || []), ...(structured.cultural_fit_evaluation || [])];
      sc = structured.final_weighted_score || 0;
      max = (weights.technical || 0) + (weights.cultural_fit || 0) + (weights.communication || 0) || 100;
    }
    setPopupTitle(`${title} (${sc}/${max})`);
    setPopupData(data);
    setShowScorePopup(true);
  };

  const sendVideoInvite = async () => {
    if (modalType === "video" && !videoAgentType) return showSnack("Please select an agent type.");
    if (modalType !== "video" && (!scheduleDate || (modalType === "voice" && (!timezone || !slotId))))
      return showSnack("Please select all fields.");
    const isHybrid = modalType === "video" && videoAgentType === "hybrid";
    const payload = [{
      job_hiring_stage_id: filterStageId,
      job_candidate_id: candidate?.id,
      ...(!isHybrid && {
        schedule_date: new Date(scheduleDate).toISOString(),
        expire_at: new Date(scheduleDate).toISOString(),
        valid_till: new Date(scheduleDate).toISOString(),
      }),
      trigger_now: false,
      ...(modalType === "video" && { video_interview_type: videoAgentType }),
      ...(modalType === "voice" && {
        current_timezone: detectedTz,
        schedule_timezone: timezone.value,
        slot_id: slotId,
      }),
    }];
    setStageLoading(true);
    const status = await dispatch(assignedStage(jobId, payload));
    setStageLoading(false);
    if (status === 200) {
      await dispatch(fetchCandidateProfile(jobId, candidateId));
      setShowVideoModal(false);
      setModalType("");
      setSlotId(null);
      setVideoAgentType("");
    }
  };

  const selectAssignedStage = async (stageId, videoDate, tz, videoInterviewType) => {
    const isHybrid = videoInterviewType === "hybrid";
    const payload = [{
      job_hiring_stage_id: stageId,
      job_candidate_id: candidate?.id,
      ...(!isHybrid && {
        schedule_date: new Date().toISOString(),
        expire_at: new Date(videoDate).toISOString(),
        valid_till: new Date(videoDate).toISOString(),
        current_timezone: tz,
        schedule_timezone: tz,
      }),
      ...(videoInterviewType && { video_interview_type: videoInterviewType }),
    }];
    setAssignProgress({ current: 0, total: 1 });
    const status = await dispatch(assignedStage(jobId, payload));
    if (status === 200) {
      await dispatch(fetchCandidateProfile(jobId, candidateId));
      setAssignProgress({ current: 1, total: 1 });
      setIsOpen(false);
    } else {
      showSnack("Failed to assign stage");
    }
    setAssignProgress({ current: 0, total: 0 });
  };

  const handleStatusChange = async (status, comment) => {
    if (!candidate?.id) return;
    const statusId = status?.id;
    const statusName = status?.name;
    if (!statusId) return showSnack("Invalid hiring status");
    try {
      setStatusLoading(true);
      const res = await dispatch(updateCandidateDetails(jobId, candidate.id, { hiring_status_id: statusId }));
      if (res?.success) {
        if (comment && comment.trim()) {
          const noteText = `Hiring status changed from "${hiringStatusName}" to "${statusName}".\n\n${comment.trim()}`;
          const noteRes = await dispatch(postCandidateComment(jobId, candidate.id, noteText, []));
          if (noteRes?.success && noteRes?.data) setNotes((prev) => [noteRes.data, ...prev]);
        }
        await dispatch(fetchCandidateProfile(jobId, candidate.id));
        await dispatch(fetchJobById(jobId));
        showSnack(`Status updated to ${statusName} successfully`, "success");
      } else {
        showSnack(res?.message || `Failed to update status`);
      }
    } catch (err) {
      showSnack(err?.message || "Unexpected error");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    const { status, comment } = statusChangeModal;
    if (!comment.trim()) { showSnack("Please add a comment before updating the status."); return; }
    await handleStatusChange(status, comment);
    setStatusChangeModal({ open: false, status: null, comment: "" });
  };

  const handleOpenSharePopup = () => {
    if (!shareData) { showSnack("Failed to generate share link"); return; }
    setShowSharePopup(true);
  };

  const handleSendInvite = async () => {
    if (!shareEmails.length) return showSnack("Please enter at least one email");
    setShareLoading(true);
    const res = await dispatch(sendShareEmail({
      share_token: shareData.share_token,
      recipient_emails: shareEmails.map((i) => i.value),
    }));
    setShareLoading(false);
    if (res.success) {
      showSnack("Invite sent successfully!", "success");
      setShareEmails([]);
      setShowSharePopup(false);
    } else {
      showSnack(res.message || "Failed to send invite");
    }
  };

  const toggleOnboardingForm = (id) =>
    setExpandedOnboardingForms((p) => ({ ...p, [id]: !p[id] }));

  const handleFlowSearch = (input) => {
    if (flowSearchDebounceRef.current) clearTimeout(flowSearchDebounceRef.current);
    flowSearchDebounceRef.current = setTimeout(() => {
      setOnboardingFlowsLoading(true);
      dispatch(fetchFlows(1, 50, input ? `search=${encodeURIComponent(input)}` : "")).then((res) => {
        setOnboardingFlows(res?.data || []);
        setOnboardingFlowsLoading(false);
      });
    }, 400);
  };

  const handleViewOnboardingRun = async (runId) => {
    setOnboardingViewModal({ open: true, runId, data: null, loading: true });
    const res = await dispatch(getRecruiterRunView(runId));
    setOnboardingViewModal({ open: true, runId, data: res?.data || res || null, loading: false });
  };

  const handleOnboardingRequestChanges = (runId) =>
    setOnboardingCommentModal({ open: true, runId, value: "" });

  const handleSubmitOnboardingRequestChanges = async () => {
    const { runId, value } = onboardingCommentModal;
    if (!value.trim()) { showSnack("Please enter a message."); return; }
    setOnboardingCommentModal((m) => ({ ...m, open: false }));
    setOnboardingViewModal((m) => ({ ...m, loading: true }));
    await dispatch(requestRunChanges(runId, { comment: value.trim(), send_email: true }));
    const res = await dispatch(getRecruiterRunView(runId));
    setOnboardingViewModal((m) => ({ ...m, data: res?.data || res || null, loading: false }));
    showSnack("Change request sent.", "success");
  };

  const handleCompleteOnboardingReview = async (runId) => {
    setOnboardingViewModal((m) => ({ ...m, loading: true }));
    await dispatch(completeRunReview(runId));
    await dispatch(fetchCandidateProfile(jobId, candidateId));
    setOnboardingViewModal({ open: false, runId: null, data: null, loading: false });
    showSnack("Onboarding marked as completed!", "success");
  };

  const openChatPanel = (tab = "messages") => {
    setChatActiveTab(tab);
    setChatPanelOpen(true);
  };

  // Open the chat panel when arriving from the candidates list popup (e.g. after sending a message)
  useEffect(() => {
    const tab = location.state?.openChatTab;
    if (tab && !isSharedReport) openChatPanel(tab);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load tab data when tab becomes active ─────────────────────────
  useEffect(() => {
    if (!chatPanelOpen || !jobId || !candidateId) return;
    if (chatActiveTab === "notes") {
      setNotesLoading(true);
      dispatch(fetchCandidateComments(jobId, candidateId)).then((res) => {
        setNotes(res?.data?.items || res?.data || []);
        setNotesLoading(false);
      });
    }
    if (chatActiveTab === "files") {
      setFilesLoading(true);
      dispatch(fetchCandidateFiles(jobId, candidateId)).then((res) => {
        setCandidateFiles(res?.data?.items || res?.data || []);
        setFilesLoading(false);
      });
    }
    if (chatActiveTab === "activity") {
      setActivityLoading(true);
      dispatch(fetchCandidateActivity(jobId, candidateId)).then((res) => {
        setActivity(res?.data?.items || res?.data || []);
        setActivityLoading(false);
      });
    }
  }, [chatActiveTab, chatPanelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notes handlers ────────────────────────────────────────────────
  const handlePostNote = async () => {
    if ((!noteBody.trim() && notePendingFiles.length === 0) || notePosting) return;
    setNotePosting(true);
    const res = await dispatch(postCandidateComment(jobId, candidateId, noteBody, notePendingFiles));
    setNotePosting(false);
    if (res?.success && res?.data) {
      setNotes((prev) => [res.data, ...prev]);
      setNoteBody("");
      setNotePendingFiles([]);
    }
  };

  const handleDeleteNote = (commentId) => {
    setDeleteNoteId(commentId);
  };

  const handleConfirmDeleteNote = async () => {
    const commentId = deleteNoteId;
    setDeleteNoteId(null);
    const res = await dispatch(deleteCandidateComment(jobId, candidateId, commentId));
    if (res?.success) setNotes((prev) => prev.filter((n) => n.id !== commentId));
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditNoteBody(note.body_text || "");
  };

  const handleSaveEditNote = async () => {
    if (!editNoteBody.trim() || !editingNoteId) return;
    const res = await dispatch(patchCandidateComment(jobId, candidateId, editingNoteId, editNoteBody));
    if (res?.success && res?.data) {
      setNotes((prev) => prev.map((n) => n.id === editingNoteId ? res.data : n));
      setEditingNoteId(null);
      setEditNoteBody("");
    }
  };

  const handleNoteFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (!newFiles.length) return;
    setNotePendingFiles((prev) => [...prev, ...newFiles].slice(0, 10));
    e.target.value = "";
  };

  const downloadResume = () => {
    if (isSharedReport) { setShowAccessModal(true); return; }
    if (candidate?.resume_url) {
      const url = candidate.resume_url;
      const filename = url.split("/").pop();
      const newTab = window.open(url, "_blank");
      if (newTab) {
        const a = newTab.document.createElement("a");
        a.href = url; a.download = filename;
        newTab.document.body.appendChild(a);
        a.click();
        newTab.document.body.removeChild(a);
      }
    }
  };

  // ── Loading state ─────────────────────────────────────────────────
  if (loading || !candidate) {
    return (
      <div className="cpv2-root">
        <div className="cpv2-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading candidate profile...</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived display values ────────────────────────────────────────
  const hiringStatusName = candidate?.hiring_status?.name || "In Progress";
  const statusVisual = getStatusVisual(hiringStatusName);
  const isOnline = chatConversation?.candidate_online === true;
  const unreadCount = chatConversation?.unread_count || 0;

  const scoreRingPercent = Math.min(score, 100);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="cpv2-root">
      {/* Stage loader dialog */}
      <Dialog
        className="schedule-loader"
        open={stageLoading}
        onClose={() => {}}
        PaperProps={{ sx: { boxShadow: "none" } }}
      >
        <div className="spinner-border" role="status" />
      </Dialog>

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div className="cpv2-topbar">
        <div className="cpv2-topbar__left">
          {!isSharedReport && (
            <button className="cpv2-topbar__back" onClick={() => navigate(`/jobs/${jobId}/candidates`)}>
              <IconBack />
            </button>
          )}
          <span className="cpv2-topbar__breadcrumb">
            Candidates &nbsp;·&nbsp; <b>{latestJob?.title || "Job"}</b>
          </span>
        </div>

        <div className="cpv2-topbar__actions">
          {!isSharedReport && (
            <>
              <button
                className="cpv2-topbar__btn"
                onClick={() => {
                  if (!latestJob?.enable_candidate_report_share) {
                    navigate(`/jobs/${jobId}/settings`); return;
                  }
                  handleOpenSharePopup();
                }}
                title={latestJob?.enable_candidate_report_share ? "Share report" : "Enable sharing in Job Settings"}
              >
                <IconShare /> Share
              </button>

              <button
                className="cpv2-topbar__btn"
                onClick={() => { setPreselectStage(null); setIsOpen(true); }}
              >
                Move Stage
              </button>

              {/* Initiate / View Onboarding */}
              {candidate?.onboarding?.run_id ? (
                canReadOnboarding && (
                <div className="d-flex align-items-center">
                  <p style={{fontSize:'12px', color:'#1e293b'}}>Onboarding :</p>&nbsp;
                  <button
                    className="cpv2-topbar__btn cpv2-topbar__btn--onboarding"
                    onClick={() => handleViewOnboardingRun(candidate.onboarding.run_id)}
                  >
                    View
                    <IconExternalLink />
                  </button>
                </div>
                )
              ) : canInitiateOnboarding ? (
                <button
                  className="cpv2-topbar__btn cpv2-topbar__btn--onboarding"
                  disabled={onboardingFlowsLoading || hiringStatusName === "Rejected"}
                  onClick={() => {
                    setOnboardingForm({ flowId: "", joiningDate: "" });
                    setOnboardingAdditionalDocs([]);
                    setOnboardingModalOpen(true);
                    setOnboardingFlowsLoading(true);
                    dispatch(fetchFlows(1, 50)).then((res) => {
                      setOnboardingFlows(res?.data || []);
                      setOnboardingFlowsLoading(false);
                    });
                  }}
                  style={{cursor : hiringStatusName === "Rejected" ? "not-allowed" : "pointer", color : hiringStatusName === "Rejected" ? "#94a3b8" : "", backgroundColor : hiringStatusName === "Rejected" ? "#f1f5f9" : "" , borderColor : hiringStatusName === "Rejected" ? "#e2e8f0" : ""}}
                >
                  <img src={hiringStatusName === "Rejected" ? "/images/send-gray.svg" : "/images/send.svg"} alt="" width={13} height={13} />
                  Initiate Onboarding
                </button>
              ) : null}

              {/* Hiring status dropdown */}
              <div style={{ position: "relative" }} ref={statusDropdownRef}>
                <button
                  className="cpv2-status-btn"
                  style={{ color: statusVisual.color, background: statusVisual.bg, border: `1px solid ${statusVisual.border}` }}
                  onClick={() => setStatusDropdownOpen((o) => !o)}
                  disabled={statusLoading}
                >
                  {statusLoading
                    ? <span className="spinner-border" style={{ width: 12, height: 12, borderWidth: 2 }} />
                    : hiringStatusName}
                  &nbsp;<IconChevronDown />
                </button>
                {statusDropdownOpen && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 4px)", right: 0,
                    background: "#fff", border: "1px solid #e4e9f0", borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 600, minWidth: 150,
                    padding: "4px 0",
                  }}>
                    {hiringStatuses.filter((s) => s.name !== hiringStatusName).length === 0 ? (
                      <div style={{ padding: "8px 16px", fontSize: 12, color: "#8a9199", textAlign: "center" }}>
                        No other statuses available
                      </div>
                    ) : (
                      hiringStatuses.filter((s) => s.name !== hiringStatusName).map((s) => (
                        <div
                          key={s.id}
                          style={{ padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 500,
                            color: "#1e293b", transition: "background 0.12s" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          onClick={() => { setStatusChangeModal({ open: true, status: s, comment: "" }); setStatusDropdownOpen(false); }}
                        >
                          {s.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Info bar ─────────────────────────────────────────────── */}
      <div className="cpv2-infobar">
        <div className="cpv2-infobar__avatar-wrap">
          <div className="cpv2-infobar__avatar">
            {candidate.name?.charAt(0).toUpperCase() || "U"}
          </div>
          {hiringStatusName !== "In Progress" && (
            <span className="cpv2-infobar__status-badge" style={{ background: statusVisual.color, color: "#fff" }}>
              {hiringStatusName}
            </span>
          )}
        </div>

        <div className="cpv2-infobar__info">
          <h2 className="cpv2-infobar__name">{candidate?.name}</h2>
          <div className="cpv2-infobar__subtitle">
            {candidate?.job?.title && <span>{candidate.job.title}</span>}
            {candidate?.years_of_experience && (
              <><span className="dot" /><span>{candidate.years_of_experience} yrs exp</span></>
            )}
            {candidate?.qualifications && (
              <><span className="dot" /><span>{candidate.qualifications}</span></>
            )}
            {candidate?.candidate_type && (
              <><span className="dot" /><span>{candidate.candidate_type}</span></>
            )}
          </div>
        </div>

        <div className="cpv2-infobar__contacts">
          {!hideSensitive && candidate?.email && (
            <div className="cpv2-infobar__contact">
              <IconMail /><a className="email" href={`mailto:${candidate.email}`}>{candidate.email}</a>
            </div>
          )}
          {!hideSensitive && candidate?.phone_number && (
            <div className="cpv2-infobar__contact">
              <IconPhoneSmall />{candidate.phone_number}
            </div>
          )}
          {candidate?.location && (
            <div className="cpv2-infobar__contact">
              <IconLocation />{candidate.location}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────── */}
      <div className="cpv2-body">

        {/* ── Sidebar ─────────────────────────────────────────── */}
        <div className="cpv2-sidebar">

          {/* Evaluation Variation Fit */}
          {Array.isArray(candidate?.candidate_variant_fit) && candidate.candidate_variant_fit.length > 0 && (
            <div className="cpv2-sb-section">
              <div className="cpv2-sb-section__title">Evaluation Fit</div>
              <div className="cpv2-variant-fit">
                {candidate.candidate_variant_fit.map((fit, i) => {
                  const isNoMatch = fit === "NO_MATCH";
                  const label = isNoMatch ? "No Match" : (candidate.candidate_variant_fit_names?.[i] || fit);
                  return (
                    <span
                      key={i}
                      className={`cpv2-variant-fit__tag${isNoMatch ? " cpv2-variant-fit__tag--no-match" : ""}`}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
              {candidate?.candidate_variant_fit_rationale && (
                <p className="cpv2-summary-text" style={{ fontSize: 12, marginTop: 6 }}>
                  {candidate.candidate_variant_fit_rationale}
                </p>
              )}
            </div>
          )}

          {/* Resume Score */}
          <div className="cpv2-sb-section">
            <div className="cpv2-sb-section__title">Resume Score</div>
            {score > 0 && (
              <div className="cpv2-score-card">
                <div className="cpv2-score-card__ring-wrap">
                  <ScoreCircle score={score} size={72} />
                </div>
                <div className="cpv2-score-card__info">
                  {(() => {
                    const label = candidate?.resume_score_label ||
                      (score >= 80 ? "Excellent match" : score >= 65 ? "Good match" : score >= 50 ? "Fair match" : score >= 35 ? "Weak match" : "Poor match");
                    const cfg = MATCH_LABEL_CONFIG[label] || { color: "#F59E0B", arrow: "up" };
                    return (
                      <div className="cpv2-score-card__match" style={{ color: cfg.color, display: "flex", alignItems: "center", gap: 4 }}>
                        {cfg.arrow === "up" ? (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                            <polyline points="16 7 22 7 22 13" />
                          </svg>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
                            <polyline points="16 17 22 17 22 11" />
                          </svg>
                        )}
                        {label}
                      </div>
                    );
                  })()}
                  {candidate?.resume_score_top_percent && (
                    <div className="cpv2-score-card__pct-label">
                      {candidate?.resume_score_top_percent && (
                        `Top ${candidate.resume_score_top_percent}% of applicants`
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <button className="cpv2-download-btn" onClick={downloadResume}>
              <IconDownload /> Download Resume
            </button>
          </div>

          {/* AI Summary */}
          {candidate?.assessment_summary && (
            <div className="cpv2-sb-section">
              <div className="cpv2-sb-section__title" style={{ display: "flex", alignItems: "center", gap: 6, justifyContent:'unset' }}>
                <IconSparkle />AI Summary
              </div>
              <p className="cpv2-summary-text cpv2-summary-text--clamp">
                {candidate.assessment_summary}
              </p>
              <button
                className="cpv2-summary-view-more"
                onClick={() => setShowSummaryPopup(true)}
              >
                View More
              </button>
            </div>
          )}

          {/* Skills */}
          {candidate?.skills?.length > 0 && (
            <div className="cpv2-sb-section">
              <div className="cpv2-sb-section__title">
                Skills
                <span className="cpv2-sb-section__title-count">{candidate.skills.length}</span>
              </div>
              <div className="cpv2-skills">
                {candidate.skills.slice(0, 8).map((s, i) => (
                  <span className="cpv2-skills__tag" key={i}>{s}</span>
                ))}
                {candidate.skills.length > 8 && (
                  <span
                    ref={skillsMoreRef}
                    className="cpv2-skills__tag cpv2-skills__tag--more"
                    onMouseEnter={() => {
                      const rect = skillsMoreRef.current?.getBoundingClientRect();
                      if (rect) setSkillsTooltipPos({ bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right });
                    }}
                    onMouseLeave={() => setSkillsTooltipPos(null)}
                  >
                    +{candidate.skills.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Views count */}
          {shareData && (
            <div className="cpv2-sb-section" style={{ fontSize: 12, color: "#8a9199" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <img src="/images/eye-black-icon.svg" alt="" width={13} height={13} style={{ opacity: 0.4 }} />
                {shareData?.view_count || 0} profile views
              </span>
            </div>
          )}

        </div>

        {/* ── Main ─────────────────────────────────────────────── */}
        <div className="cpv2-main">

          {/* Stage cards row */}
          <div className="cpv2-stages">
            <div className="cpv2-stages__inner">
              {visibleStageCards.map((card) => {
                const { status, score: stageScore } = getStageInfo(card.stageKey);
                const isActive = testType === card.typeIndex;

                return (
                  <div
                    key={card.typeIndex}
                    className={`cpv2-stage-card${isActive ? " active" : ""}`}
                    onClick={() => changeType(card.typeIndex)}
                  >
                    <div className="cpv2-stage-card__top">
                      <div className="cpv2-stage-card__icon">{STAGE_ICONS[card.typeIndex]}</div>
                      {stageScore !== null ? (
                        <>
                        {STAGE_CARDS?.[card.typeIndex]?.label === "Assessments" ? (
                            <span style={{ color: "#c0c7cf", fontSize: 16, fontWeight: 500, lineHeight: 1 }}>—</span>
                          ) : (
                            <div className="cpv2-stage-card__score" style={{ ...getScoreBadgeStyle(stageScore), borderRadius: 6, padding: "2px 7px", fontSize: 12, fontWeight: 700 }}>
                              {stageScore.toFixed(0)}%
                            </div>
                          )
                        }
                        </>
                      ) : status === "ONGOING" ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#2F80ED", fontSize: 11, fontWeight: 600 }}>
                          <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="#2F80ED" /></svg>
                          Active
                        </span>
                      ) : status === "SCHEDULED" ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 3, color: "#F59E0B", fontSize: 11, fontWeight: 600 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                          Soon
                        </span>
                      ) : status === "EXPIRED" || status === "FAILED" ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 3, color: "#ef4444", fontSize: 11, fontWeight: 600 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                          {status === "EXPIRED" ? "Expired" : "Failed"}
                        </span>
                      ) : (
                        <span style={{ color: "#c0c7cf", fontSize: 16, fontWeight: 500, lineHeight: 1 }}>—</span>
                      )}
                    </div>
                    <div className="cpv2-stage-card__name">{card.label}</div>
                    <div
                      className="cpv2-stage-card__status"
                      style={{ color: STATUS_COLORS[status] || "#8a9199", display: "flex", alignItems: "center", gap: 4 }}
                    >
                      {getStatusLabel(status)}
                      {card.stageKey === "Voice Interview" && isDynamicVoiceAssistantEnabled && voiceFitLabel && FIT_LABEL_CONFIG[voiceFitLabel] && (
                        <span style={{ color: FIT_LABEL_CONFIG[voiceFitLabel].color, fontWeight: 500 , fontSize: 11, backgroundColor: FIT_LABEL_CONFIG[voiceFitLabel].bgColor, padding: "2px 6px", borderRadius: 4 }}>
                          {FIT_LABEL_CONFIG[voiceFitLabel].title}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stage content */}
          <div className="cpv2-stage-content">
            {testType === 0 && (
              <PreScreeningStage
                candidate={candidate}
                preQualifyingReport={preQualifyingReport}
                tabLoading={tabLoading}
                formatExpiry={formatExpiry}
                isSharedReport={isSharedReport}
                setPreselectStage={setPreselectStage}
                setIsOpen={setIsOpen}
              />
            )}
            {testType === 1 && (
              <VoiceInterviewStage
                candidate={candidate}
                voiceSession={voiceSession}
                currentStage={currentStage}
                currentStatus={currentStatus}
                tabLoading={tabLoading}
                isSharedReport={isSharedReport}
                handleVoiceCall={handleVoiceCall}
                handleVoiceRefresh={handleVoiceRefresh}
                formatDateTime={formatDateTime}
                onScheduleVoice={handleScheduleVoice}
                loadingCall={loadingCall}
                refreshing={refreshing}
                audioLoading={audioLoading}
                setAudioLoading={setAudioLoading}
                expandSummary={expandSummary}
                setExpandSummary={setExpandSummary}
                expandRecommendation={expandRecommendation}
                setExpandRecommendation={setExpandRecommendation}
                expandFitRationale={expandFitRationale}
                setExpandFitRationale={setExpandFitRationale}
                handleScoreClick={handleScoreClick}
                setShowPhoneCheckModal={setShowPhoneCheckModal}
                initiatingVideoCall={initiatingVideoCall}
                hideVoiceCallActions={isDynamicVoiceCallMode}
              />
            )}
            {testType === 2 && (
              <AssessmentStage
                candidate={candidate}
                currentStage={currentStage}
                currentStatus={currentStatus}
                assessmentInviteDetails={assessmentInviteDetails}
                latestJob={latestJob}
                tabLoading={tabLoading}
                isSharedReport={isSharedReport}
                formatDateOnly={formatDateOnly}
                formatDateTime={formatDateTime}
                navigate={navigate}
                setShowVideoModal={setShowVideoModal}
                setModalType={setModalType}
                assessmentStatus={assessmentStatus}
                initiatingVideoCall={initiatingVideoCall}
                inlineReportId={inlineReportId}
                setInlineReportId={setInlineReportId}
                showInlineReport={showInlineReport}
                setShowInlineReport={setShowInlineReport}
                useSharedToken={props.useSharedToken}
              />
            )}
            {testType === 3 && (
              <VideoInterviewStage
                videoSession={videoSession}
                currentStage={currentStage}
                currentStatus={currentStatus}
                tabLoading={tabLoading}
                isSharedReport={isSharedReport}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                formatDateOnly={formatDateOnly}
                setShowVideoModal={setShowVideoModal}
                setModalType={setModalType}
                initiatingVideoCall={initiatingVideoCall}
                proctoring={proctoring}
                cheatingFrames={cheatingFrames}
                showAllCheatingImages={showAllCheatingImages}
                setShowAllCheatingImages={setShowAllCheatingImages}
                selectedProctorIndex={selectedProctorIndex}
                setSelectedProctorIndex={setSelectedProctorIndex}
                setOpenProctorLightbox={setOpenProctorLightbox}
                setSelectedProctorImage={setSelectedProctorImage}
              />
            )}
          </div>
        </div>

        {/* ── Icon rail ────────────────────────────────────────── */}
        {!isSharedReport && (
          <div className="cpv2-icon-rail">
            <button
              className={`cpv2-rail-btn${chatPanelOpen && chatActiveTab === "messages" ? " active" : ""}`}
              title="Messages"
              onClick={() => openChatPanel("messages")}
            >
              <IconMsg />
              {unreadCount > 0 && (
                <span className="cpv2-rail-btn__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </button>
            <button
              className={`cpv2-rail-btn${chatPanelOpen && chatActiveTab === "activity" ? " active" : ""}`}
              title="Activity"
              onClick={() => openChatPanel("activity")}
            >
              <IconActivity />
            </button>
            <button
              className={`cpv2-rail-btn${chatPanelOpen && chatActiveTab === "notes" ? " active" : ""}`}
              title="Notes"
              onClick={() => openChatPanel("notes")}
            >
              <IconNotes />
            </button>
            <button
              className={`cpv2-rail-btn${chatPanelOpen && chatActiveTab === "files" ? " active" : ""}`}
              title="Files"
              onClick={() => openChatPanel("files")}
            >
              <IconFiles />
            </button>
          </div>
        )}
      </div>

      {/* ── Chat panel ───────────────────────────────────────────── */}
      {!isSharedReport && (
        <div className={`cpv2-chat-panel${chatPanelOpen ? " open" : ""}`}>
          <div className="cpv2-chat-panel__header">
            <div className="cpv2-chat-panel__avatar">
              {candidate.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="cpv2-chat-panel__hinfo">
              <div className="cpv2-chat-panel__hname">{candidate?.name}</div>
              <div className="cpv2-chat-panel__hstatus">
                <span className={`cpv2-chat-panel__online-dot${isOnline ? " online" : ""}`} />
                {formatLastActive(chatConversation?.candidate_last_active_at, isOnline)}
              </div>
            </div>
            <button className="cpv2-chat-panel__close" onClick={() => setChatPanelOpen(false)}>
              ×
            </button>
          </div>

          <div className="cpv2-chat-panel__tabs">
            {[
              { key: "messages", label: "Messages", count: unreadCount },
              { key: "activity", label: "Activity", count: 0 },
              { key: "notes", label: "Notes", count: 0 },
              { key: "files", label: "Files", count: 0 },
            ].map((tab) => (
              <button
                key={tab.key}
                className={`cpv2-chat-panel__tab${chatActiveTab === tab.key ? " active" : ""}`}
                onClick={() => setChatActiveTab(tab.key)}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="cpv2-chat-panel__tab-count">{tab.count > 99 ? "99+" : tab.count}</span>
                )}
              </button>
            ))}
          </div>

          <div className="cpv2-chat-panel__body">
            {/* Messages — always mounted so WS stays connected for badge updates */}
            <div
              className="cpv2-chat-panel__msg-wrap"
              style={{ display: chatActiveTab === "messages" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}
            >
              <RecruiterChat
                jobCandidateId={candidateId}
                candidateName={candidate?.name}
                convLoading={false}
                isVisible={chatPanelOpen && chatActiveTab === "messages"}
              />
            </div>

            {/* Activity tab */}
            {chatActiveTab === "activity" && (
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
                {activityLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                    <div className="spinner-border spinner-border-sm" role="status" />
                  </div>
                ) : activity.length === 0 ? (
                  <div className="cpv2-chat-panel__empty">
                    <IconActivity />
                    <span>No activity yet</span>
                    <span style={{ fontSize: 11 }}>Candidate interactions will appear here</span>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {activity.map((item, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, paddingBottom: 12 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                          <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#1c2b4a", marginTop: 3 }} />
                          {i < activity.length - 1 && <div style={{ width: 1, flex: 1, background: "#e4e9f0", marginTop: 4 }} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: "#1e293b", lineHeight: 1.4 }}>
                            {item.title || item.description || item.event_type || "Activity"}
                          </div>
                          {(() => {
                            const changes = item?.meta_data?.data?.changes;
                            if (!Array.isArray(changes) || changes.length === 0) return null;
                            return (
                              <div style={{ marginTop: 5, display: "flex", flexDirection: "column", gap: 4 }}>
                                {changes.map((change, ci) => (
                                  <div key={ci} style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                                    {change.label && (
                                      <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500, minWidth: 0 }}>
                                        {change.label}:
                                      </span>
                                    )}
                                    {renderActivityChangeValue(change.value_type, change.old_display)}
                                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>→</span>
                                    {renderActivityChangeValue(change.value_type, change.new_display)}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          {item.actor_name && (
                            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>by {item.actor_name}</div>
                          )}
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                            {item.created_at
                              ? new Date(item.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                              : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes tab */}
            {chatActiveTab === "notes" && (
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                {/* Notes list */}
                <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {notesLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                      <div className="spinner-border spinner-border-sm" role="status" />
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="cpv2-chat-panel__empty">
                      <IconNotes />
                      <span>No notes yet</span>
                      <span style={{ fontSize: 11 }}>Add internal notes about this candidate</span>
                    </div>
                  ) : notes.map((note) => (
                    <div key={note.id} style={{ border: "1px solid #e4e9f0", borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{note.author_name || "Recruiter"}</span>
                          {note.author_email && <div style={{ fontSize: 11, color: "#94a3b8" }}>{note.author_email}</div>}
                        </div>
                        <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0, marginLeft: 8 }}>
                          {note.created_at ? new Date(note.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>
                      </div>
                      {editingNoteId === note.id ? (
                        <div>
                          <textarea
                            value={editNoteBody}
                            onChange={(e) => setEditNoteBody(e.target.value)}
                            rows={3}
                            style={{ width: "100%", padding: "7px 9px", border: "1px solid #e4e9f0", borderRadius: 8, fontSize: 12, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                          />
                          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                            <button onClick={handleSaveEditNote} style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: "#1c2b4a", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Save</button>
                            <button onClick={() => { setEditingNoteId(null); setEditNoteBody(""); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #e4e9f0", background: "#fff", color: "#525b66", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {note.body_text && (
                            <p style={{ fontSize: 12, color: "#1e293b", margin: "0 0 8px", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {note.body_text}
                            </p>
                          )}
                          {(note.attachments || []).map((att, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e4e9f0", marginBottom: 6 }}>
                              <FileTypeIcon name={att.file_name} mimeType={att.mime_type || ''} />
                              <span style={{ fontSize: 11, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.file_name || "File"}</span>
                              {att.file_url && (
                                <button onClick={() => window.open(att.file_url, "_blank")} style={{ fontSize: 11, color: "#1c2b4a", background: "none", border: "none", cursor: "pointer", padding: 0 }}>View</button>
                              )}
                            </div>
                          ))}
                          <div style={{ display: "flex", gap: 5, margin: '4px 0 0 4px' }}>
                            <button onClick={() => handleStartEditNote(note)} style={{ fontSize: 11, color: "#1c2b4a", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500 }} title="Edit">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                            <span style={{ fontSize: 11, color: "#d1d5db" }}>·</span>
                            {/* <button onClick={() => handleDeleteNote(note.id)} style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500 }} title="Delete">
                              <img src="/images/delete-icon-gray.svg" alt="" width={14} height={14} />
                            </button> */}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {/* Note composer */}
                <div style={{ padding: "10px 14px 14px", borderTop: "1px solid #e4e9f0", flexShrink: 0, background: "#fff" }}>
                  {notePendingFiles.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                      {notePendingFiles.map((f, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", background: "#f0f4ff", borderRadius: 6, border: "1px solid #e4e9f0" }}>
                          <FileTypeIcon name={f.name} mimeType={f.type || ''} />
                          <span style={{ fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                          <button onClick={() => setNotePendingFiles((p) => p.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Write a note… (Ctrl+Enter to post)"
                    rows={2}
                    onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handlePostNote(); }}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e4e9f0", borderRadius: 8, fontSize: 12, fontFamily: "inherit", resize: "none", boxSizing: "border-box", outline: "none", marginBottom: 8 }}
                  />
                  <input ref={noteFileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif" style={{ display: "none" }} onChange={handleNoteFileChange} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <button
                      onClick={() => noteFileInputRef.current?.click()}
                      style={{ background: "none", border: "1px solid #e4e9f0", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                      </svg>
                      Attach
                    </button>
                    <button
                      onClick={handlePostNote}
                      disabled={notePosting || (!noteBody.trim() && notePendingFiles.length === 0)}
                      style={{ padding: "5px 16px", borderRadius: 6, border: "none", background: "#1c2b4a", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: (notePosting || (!noteBody.trim() && notePendingFiles.length === 0)) ? 0.5 : 1 }}
                    >
                      {notePosting ? "Posting…" : "Post"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Files tab */}
            {chatActiveTab === "files" && (
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                {filesLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                    <div className="spinner-border spinner-border-sm" role="status" />
                  </div>
                ) : candidateFiles.length === 0 ? (
                  <div className="cpv2-chat-panel__empty">
                    <IconFiles />
                    <span>No files yet</span>
                    <span style={{ fontSize: 11 }}>Resume uploads, chat attachments and manual uploads</span>
                  </div>
                ) : candidateFiles.map((file) => {
                  const srcLabels = { resume_upload: "Resume", chat_in_app: "Chat", chat_messenger: "Chat", manual: "Uploaded" };
                  const srcBg = { resume_upload: "#dbeafe", chat_in_app: "#dcfce7", chat_messenger: "#dcfce7", manual: "#fce7f3" };
                  const srcColor = { resume_upload: "#1d4ed8", chat_in_app: "#15803d", chat_messenger: "#15803d", manual: "#be185d" };
                  const sizeBytes = file.file_size_bytes || file.file_size || null;
                  const sizeStr = sizeBytes != null
                    ? (sizeBytes < 1024 ? `${sizeBytes} B` : sizeBytes < 1048576 ? `${(sizeBytes / 1024).toFixed(1)} KB` : `${(sizeBytes / 1048576).toFixed(1)} MB`)
                    : "";
                  return (
                    <div key={file.id} style={{ border: "1px solid #e4e9f0", borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 999, background: srcBg[file.source] || "#f1f5f9", color: srcColor[file.source] || "#64748b" }}>
                          {srcLabels[file.source] || file.source || "File"}
                        </span>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>
                          {file.created_at ? new Date(file.created_at).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 22, flexShrink: 0 }}>📄</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.file_name || "File"}</div>
                          {sizeStr && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sizeStr}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                          {file.file_url && (
                            <button onClick={() => window.open(file.file_url, "_blank")} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #e4e9f0", background: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#1e293b" }}>View</button>
                          )}
                          {file.file_url && (
                            <button onClick={async () => {
                              try {
                                const r = await fetch(file.file_url);
                                const blob = await r.blob();
                                const bUrl = URL.createObjectURL(blob);
                                const a = document.createElement("a"); a.href = bUrl; a.download = file.file_name || "file";
                                document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(bUrl);
                              } catch { window.open(file.file_url, "_blank"); }
                            }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #e4e9f0", background: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#1e293b" }}>↓</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FAB ──────────────────────────────────────────────────── */}
      {!isSharedReport && (
        <button
          className={`cpv2-fab${chatPanelOpen ? " panel-open" : ""}`}
          onClick={() => openChatPanel("messages")}
        >
          {unreadCount > 0 && (
            <span style={{
              position: "absolute", top: -6, right: -6,
              background: "#ef4444", color: "#fff",
              fontSize: 10, fontWeight: 700, minWidth: 18, height: 18,
              borderRadius: 999, display: "inline-flex", alignItems: "center",
              justifyContent: "center", padding: "0 5px", lineHeight: 1,
              border: "2px solid #fff",
            }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <IconMsg />
          <div className="cpv2-fab__text">
            <span>Chat with {candidate?.name?.split(" ")[0] || "Candidate"}</span>
            <span className="cpv2-fab__status">
              <span className={`cpv2-fab__dot${isOnline ? " online" : ""}`} />
              {formatLastActive(chatConversation?.candidate_last_active_at, isOnline)}
            </span>
          </div>
        </button>
      )}

      {/* ── Skills tooltip (fixed so it escapes sidebar overflow) ── */}
      {skillsTooltipPos && (
        <div
          className="cpv2-skills__tooltip-fixed"
          style={{ bottom: skillsTooltipPos.bottom, right: skillsTooltipPos.right }}
          onMouseEnter={() => {
            const rect = skillsMoreRef.current?.getBoundingClientRect();
            if (rect) setSkillsTooltipPos({ bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right });
          }}
          onMouseLeave={() => setSkillsTooltipPos(null)}
        >
          {candidate.skills.slice(8).map((s, i) => (
            <span className="cpv2-skills__tooltip-item" key={i}>{s}</span>
          ))}
        </div>
      )}

      {/* ── Proctor lightbox ─────────────────────────────────────── */}
      {openProctorLightbox && (
        <Lightbox
          images={cheatingFrames.map((f) => f.frame_url || f.image_url).filter(Boolean)}
          selectedIndex={selectedProctorIndex}
          onClose={() => setOpenProctorLightbox(false)}
        />
      )}

      {/* ── Delete Note confirmation popup ───────────────────────── */}
      {deleteNoteId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: "28px 28px 24px", width: 320, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>Delete note?</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>This action cannot be undone.</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteNoteId(null)}
                style={{ padding: "7px 18px", borderRadius: 7, border: "1px solid #e4e9f0", background: "#fff", color: "#525b66", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >Cancel</button>
              <button
                onClick={handleConfirmDeleteNote}
                style={{ padding: "7px 18px", borderRadius: 7, border: "none", background: "#ef4444", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Snackbar ─────────────────────────────────────────────── */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert onClose={snackClosed} severity={snackSeverity} sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      {/* ── Summary popup ────────────────────────────────────────── */}
      {showSummaryPopup && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal" style={{ maxWidth: 600 }}>
            <div className="jv2-modal-head">
              <h3>AI Summary</h3>
              <button className="jv2-modal-close" onClick={() => setShowSummaryPopup(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body" style={{ maxHeight: 280, overflowY: "auto" }}>
              <p style={{ textAlign: "left" }}>{candidate.assessment_summary || "No summary available."}</p>
            </div>
            <div className="jv2-modal-footer">
              <button className="jv2-modal-confirm" onClick={() => setShowSummaryPopup(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assistant modal ───────────────────────────────────────── */}
      {showAssistantModal && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal" style={{ maxWidth: 600 }}>
            <div className="jv2-modal-head">
              <h3>Make a voice call with this assistant</h3>
              <button className="jv2-modal-close" onClick={() => { if (currentAudio) currentAudio.pause(); setShowAssistantModal(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body" style={{ maxHeight: 350, overflowY: "auto", scrollbarWidth: "none" }}>
              {assistants.map((assistant, idx) => (
                <div
                  key={idx}
                  style={{ border: "1px solid #e4e9f0", borderRadius: 8, padding: 12, margin: "8px 0", cursor: "pointer" }}
                  onClick={() => setSelectedAssistantId(assistant.id)}
                >
                  {assistant.voice_clip_url && (
                    <label style={{ width: "100%", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#1e293b" }}>
                      <input
                        type="radio"
                        name="assistant"
                        value={assistant.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (currentAudio) currentAudio.pause();
                          setCurrentAudio(new Audio(assistant.voice_clip_url));
                        }}
                      />
                      {assistant.voice_name}
                    </label>
                  )}
                </div>
              ))}
            </div>
            {!isSharedReport && (
              <div className="jv2-modal-footer">
                <button className="jv2-modal-cancel" onClick={() => { if (currentAudio) currentAudio.pause(); setShowAssistantModal(false); }}>Cancel</button>
                <button
                  className="jv2-modal-confirm"
                  disabled={!selectedAssistantId}
                  onClick={async () => {
                    if (currentAudio) currentAudio.pause();
                    setShowAssistantModal(false);
                    const now = new Date();
                    const payload = [{
                      job_hiring_stage_id: filterStageId,
                      job_candidate_id: candidate?.id,
                      schedule_date: now.toISOString(),
                      expire_at: now.toISOString(),
                      valid_till: now.toISOString(),
                      assistant_id: selectedAssistant.id,
                      trigger_now: true,
                      current_timezone: detectedTz,
                      schedule_timezone: detectedTz,
                    }];
                    setLoadingCall(true);
                    await dispatch(assignedStage(jobId, payload));
                    await dispatch(fetchCandidateProfile(jobId, candidateId));
                    setLoadingCall(false);
                  }}
                >
                  Make voice call
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phone check modal ─────────────────────────────────────── */}
      {showPhoneCheckModal && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal">
            <div className="jv2-modal-head">
              <h3>Phone Number Check</h3>
              <button className="jv2-modal-close" onClick={() => setShowPhoneCheckModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body">
              <p style={{ textAlign: "center" }}>Country code is missing in the mobile number.</p>
            </div>
            <div className="jv2-modal-footer">
              <button className="jv2-modal-confirm" onClick={() => setShowPhoneCheckModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Video / Voice / Assessment schedule modal ─────────────── */}
      {showVideoModal && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal" style={{ maxWidth: 480 }}>
            <div className="jv2-modal-head">
              <h3>
                {modalType === "voice" && "Schedule a voice interview"}
                {modalType === "assessment" && "Send an assessment invite"}
                {modalType === "video" && "Schedule a video interview"}
              </h3>
              <button className="jv2-modal-close" onClick={() => { setShowVideoModal(false); setModalType(""); setVideoAgentType(""); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {modalType === "voice" && (
                <div className="modal-input-label">
                  <label>Time Zone</label>
                  <Select options={TimeZone} value={timezone} onChange={setTimezone} placeholder="Select Timezone" Time Zone styles={{borderRadius :'12px'}} />
                </div>
              )}
              {modalType === "video" && (
                <div className="modal-input-label">
                  <label>Agent Type</label>
                  <select className="form-control" value={videoAgentType} onChange={(e) => setVideoAgentType(e.target.value)} style={{borderRadius:'12px', marginTop:'8px'}}>
                    <option hidden value="">Select Agent Type</option>
                    <option value="hybrid">AI Assisted Interview</option>
                    <option value="ai_interview">AI Lead Interview</option>
                  </select>
                  {isVideoSettingsMissing && (
                    <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13, fontWeight: 500 }}>
                      You haven't added the video interview settings, please add.
                    </div>
                  )}
                </div>
              )}
              {(modalType !== "video" || videoAgentType === "ai_interview") && (
                <div className="modal-input-label">
                  <label>Due Date</label>
                  <DatePicker
                    selected={scheduleDate}
                    onChange={(date) => setScheduleDate(date)}
                    minDate={new Date()}
                    maxDate={moment().add(15, "days").toDate()}
                    dateFormat="dd-MM-yyyy"
                    className="form-control"
                    style={{borderRadius:'12px', marginTop:'8px'}}
                  />
                </div>
              )}
              {modalType === "voice" && (
                <div className="modal-input-label">
                  <label>Time Slot</label>
                  <Select
                    options={availableSlots?.map((s) => ({ label: s.display, value: s.id }))}
                    value={availableSlots?.map((s) => ({ label: s.display, value: s.id })).find((o) => o.value === slotId) || null}
                    onChange={(opt) => setSlotId(opt.value)}
                    placeholder="Select a time slot"
                    menuPlacement="top"
                    maxMenuHeight={200}
                  />
                </div>
              )}
            </div>
            {!isSharedReport && (
              <div className="jv2-modal-footer">
                <button className="jv2-modal-cancel" onClick={() => { setShowVideoModal(false); setModalType(""); setVideoAgentType(""); }}>Cancel</button>
                {isVideoSettingsMissing ? (
                  <button className="jv2-modal-confirm" onClick={() => navigate(`/jobs/${jobId}/settings`)}>
                    Video Settings
                  </button>
                ) : (
                  <button
                    className="jv2-modal-confirm"
                    disabled={
                      stageLoading ||
                      (modalType === "video" && !videoAgentType) ||
                      (modalType === "video" && videoAgentType === "ai_interview" && !scheduleDate)
                    }
                    onClick={sendVideoInvite}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Score popup ───────────────────────────────────────────── */}
      {showScorePopup && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal" style={{ maxWidth: 600 }}>
            <div className="jv2-modal-head">
              <h3>{popupTitle}</h3>
              <button className="jv2-modal-close" onClick={() => setShowScorePopup(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body" style={{ maxHeight: 400, overflowY: "auto" }}>
              {popupData.length > 0 ? (
                popupData.map((item, i) => (
                  <div key={i} style={{ borderBottom: "1px solid #e4e9f0", paddingBottom: 12, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                      <p style={{ textAlign: "left", fontWeight: 600, color: "#1e293b", flex: 1 }}>{i + 1}. {item.question}</p>
                      <p style={{ whiteSpace: "nowrap", fontWeight: 600, color: "#f75891" }}>Score: {item.score}/5</p>
                    </div>
                    <p style={{ textAlign: "left", color: "#525b66" }}>Answer summary: {item.answer_summary}</p>
                  </div>
                ))
              ) : (
                <p style={{ textAlign: "center" }}>No data available.</p>
              )}
            </div>
            <div className="jv2-modal-footer">
              <button className="jv2-modal-confirm" onClick={() => setShowScorePopup(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share popup ───────────────────────────────────────────── */}
      {showSharePopup && (
        <div className="share-modal-overlay">
          <div className="share-modal">
            <div className="share-modal-header">
              <h4>{candidate?.name || "Candidate"}</h4>
              <span onClick={() => { setShareEmails(""); setShowSharePopup(false); }}>
                <img src="/images/close-icon.svg" alt="close" className="cursor-pointer" />
              </span>
            </div>
            {shareLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: 120 }}>
                <div className="spinner-border" role="status" />
              </div>
            ) : shareData ? (
              <>
                <h3>Share report</h3>
                <CreatableSelect
                  className="email-input"
                  isMulti
                  placeholder="Enter email ids"
                  value={shareEmails}
                  onChange={(vals) => setShareEmails(vals || [])}
                  styles={{ container: (base) => ({ ...base, width: "100%" }) }}
                />
                <button className="send-invite-btn" onClick={handleSendInvite} disabled={shareLoading}>
                  {shareLoading ? "Sending email..." : "Send email"}
                </button>
                <div className="share-footer">
                  <div style={{ width: 200 }}>
                    <button className="copy-link-btn" onClick={() => { navigator.clipboard.writeText(shareData.share_url); showSnack("Link copied!", "success"); }}>
                      <img src="/images/copy-outline-wight-icon.svg" alt="" width={16} height={16} />
                      &nbsp; Copy Link
                    </button>
                    <div className="access-code">
                      Share access code <strong>{shareData.access_code}</strong> to view link
                    </div>
                  </div>
                  <button className="done-btn" onClick={() => { setShareEmails(""); setShowSharePopup(false); }}>
                    Close
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ── Access modal ─────────────────────────────────────────── */}
      {showAccessModal && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal">
            <div className="jv2-modal-head">
              <h3>Access Restricted</h3>
              <button className="jv2-modal-close" onClick={() => setShowAccessModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="jv2-modal-body">
              <p style={{ textAlign: "center" }}>You don't have the required access to download this resume.</p>
            </div>
            <div className="jv2-modal-footer">
              <button className="jv2-modal-confirm" onClick={() => setShowAccessModal(false)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding initiate modal ─────────────────────────────── */}
      {onboardingModalOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(26,26,46,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}
          onClick={() => setOnboardingModalOpen(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 460, boxShadow: "0 8px 40px rgba(26,26,46,0.18)", maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1a1a2e" }}>Onboarding details</h3>
              <img src="/images/close-icon.svg" alt="Close" style={{ cursor: "pointer", width: 18, height: 18 }} onClick={() => setOnboardingModalOpen(false)} />
            </div>
            <div style={{ maxHeight: "65vh", overflowY: "auto", scrollbarWidth: "none" }}>
              <div style={{ fontSize: 13, color: "#525b66", marginBottom: 20, padding: "10px 14px", background: "#f4f5f7", borderRadius: 8 }}>
                <strong style={{ color: "#1a1a2e" }}>{candidate?.name}</strong>
                <br />
                <span style={{ fontSize: 12, color: "#8a9199" }}>{candidate?.email}</span>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#525b66", marginBottom: 5 }}>
                  Select Flow <span style={{ color: "#f75891" }}>*</span>
                </label>
                <Select
                  options={onboardingFlows.map((f) => ({ value: f.id, label: f.name }))}
                  value={onboardingForm.flowId ? { value: onboardingForm.flowId, label: onboardingFlows.find((f) => f.id === onboardingForm.flowId)?.name || "" } : null}
                  onChange={(opt) => setOnboardingForm((f) => ({ ...f, flowId: opt ? opt.value : "" }))}
                  onInputChange={handleFlowSearch}
                  filterOption={null}
                  placeholder="Select an onboarding flow…"
                  isClearable
                  isLoading={onboardingFlowsLoading}
                  noOptionsMessage={() => "No active flows found."}
                  styles={{ container: (base) => ({ ...base, width: "100%" }), borderRadius:'12px', marginTop:'8px' }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#525b66", marginBottom: 5 }}>
                  Joining Date <span style={{ color: "#f75891" }}>*</span>
                </label>
                <input
                  type="date"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #e4e9f0", borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit" }}
                  value={onboardingForm.joiningDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setOnboardingForm((f) => ({ ...f, joiningDate: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 3 }}>
                  Additional documents <span style={{ fontSize: 12, fontWeight: 400, color: "#8a9199" }}>(optional)</span>
                </div>
                {onboardingAdditionalDocs.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
                    {onboardingAdditionalDocs.map((doc, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#525b66", background: "#f4f5f7", borderRadius: 6, padding: "6px 10px" }}>
                        <FileTypeIcon name={doc.name} mimeType="" />
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</span>
                        <select
                          style={{ fontSize: 11, padding: "2px 6px", border: "1px solid #e4e9f0", borderRadius: 4, background: "#fff" }}
                          value={doc.mode}
                          onChange={(e) => setOnboardingAdditionalDocs((p) => p.map((d, j) => j === i ? { ...d, mode: e.target.value } : d))}
                        >
                          <option value="read">Read</option>
                          <option value="read & accept">Read &amp; Accept</option>
                        </select>
                        <button style={{ fontSize: 13, color: "#f75891", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                          onClick={() => setOnboardingAdditionalDocs((p) => p.filter((_, j) => j !== i))}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  style={{ border: `2px dashed ${dragActive ? "#f75891" : "#d1d5db"}`, borderRadius: 10, padding: "28px 20px", textAlign: "center", cursor: "pointer", background: dragActive ? "#fff0f5" : "#fafafa", transition: "border-color 0.2s, background 0.2s" }}
                  onClick={() => additionalDocInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={async (e) => {
                    e.preventDefault(); setDragActive(false);
                    const files = Array.from(e.dataTransfer.files);
                    const uploaded = [];
                    for (const file of files) {
                      const fd = new FormData();
                      fd.append("file", file); fd.append("bucket", "org");
                      fd.append("detail", `${(file.size / 1024).toFixed(1)} KB`);
                      fd.append("badge", "candidate");
                      fd.append("meta_data", JSON.stringify({ candidate_specific: true }));
                      const res = await dispatch(uploadDocument(fd));
                      if (res?.success) uploaded.push({ id: res?.data?.id ?? res?.data?.data?.id ?? null, name: file.name, mode: "read" });
                    }
                    setOnboardingAdditionalDocs((p) => [...p, ...uploaded]);
                  }}
                >
                  <span style={{ fontSize: 13, color: "#525b66", fontWeight: 500 }}>Upload additional document</span>
                  <input
                    ref={additionalDocInputRef} type="file" multiple style={{ display: "none" }}
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      const uploaded = [];
                      for (const file of files) {
                        const fd = new FormData();
                        fd.append("file", file); fd.append("bucket", "org");
                        fd.append("detail", `${(file.size / 1024).toFixed(1)} KB`);
                        fd.append("badge", "candidate");
                        fd.append("meta_data", JSON.stringify({ candidate_specific: true }));
                        const res = await dispatch(uploadDocument(fd));
                        if (res?.success) uploaded.push({ id: res?.data?.id ?? res?.data?.data?.id ?? null, name: file.name, mode: "read" });
                      }
                      setOnboardingAdditionalDocs((p) => [...p, ...uploaded]);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #e4e9f0", background: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#525b66" }} onClick={() => setOnboardingModalOpen(false)}>Cancel</button>
              <button
                style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #f75891", background: "#f75891", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#fff", opacity: onboardingLoading ? 0.6 : 1 }}
                disabled={onboardingLoading || !onboardingForm.flowId || !onboardingForm.joiningDate}
                onClick={async () => {
                  setOnboardingLoading(true);
                  const res = await dispatch(initiateOnboarding({
                    flow_id: onboardingForm.flowId,
                    candidate_id: candidateId,
                    joining_date: onboardingForm.joiningDate,
                    channel: "email",
                    job_id: jobId,
                    additional_document_ids: onboardingAdditionalDocs.map((d) => d.id),
                    additional_document_modes: Object.fromEntries(onboardingAdditionalDocs.map((d) => [d.id, d.mode || "read"])),
                  }));
                  setOnboardingLoading(false);
                  if (res) {
                    setOnboardingModalOpen(false);
                    setOnboardingAdditionalDocs([]);
                    showSnack("Onboarding initiated successfully!", "success");
                    window.location.reload();
                  } else {
                    showSnack("Failed to initiate onboarding.");
                  }
                }}
              >
                {onboardingLoading ? "Initiating..." : "Initiate onboarding"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding request changes modal ─────────────────────── */}
      {onboardingCommentModal.open && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(26,26,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 16 }}
          onClick={() => setOnboardingCommentModal((m) => ({ ...m, open: false }))}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 24, width: "100%", maxWidth: 420, boxShadow: "0 8px 40px rgba(26,26,46,0.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1a1a2e" }}>Request Changes</h3>
              <img src="/images/close-icon.svg" alt="Close" style={{ cursor: "pointer", width: 18, height: 18 }} onClick={() => setOnboardingCommentModal((m) => ({ ...m, open: false }))} />
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#525b66", display: "block", marginBottom: 6 }}>Message to the candidate</label>
            <textarea
              autoFocus rows={4}
              style={{ width: "100%", padding: "9px 12px", border: "1px solid #e4e9f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }}
              placeholder="Describe what needs to be corrected…"
              value={onboardingCommentModal.value}
              onChange={(e) => setOnboardingCommentModal((m) => ({ ...m, value: e.target.value }))}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e4e9f0", background: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#525b66", minWidth: 80 }} onClick={() => setOnboardingCommentModal((m) => ({ ...m, open: false }))}>Cancel</button>
              <button style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #f75891", background: "#f75891", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#fff", minWidth: 80 }} onClick={handleSubmitOnboardingRequestChanges}>Send</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding view modal ─────────────────────────────────── */}
      {onboardingViewModal.open && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(26,26,46,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}
          onClick={() => setOnboardingViewModal({ open: false, runId: null, data: null, loading: false })}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 540, boxShadow: "0 8px 40px rgba(26,26,46,0.18)", maxHeight: "80vh", overflowY: "auto", scrollbarWidth: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1a1a2e" }}>Candidate Progress</h3>
              <img src="/images/close-icon.svg" alt="Close" style={{ cursor: "pointer", width: 18, height: 18 }} onClick={() => setOnboardingViewModal({ open: false, runId: null, data: null, loading: false })} />
            </div>
            {onboardingViewModal.loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <div className="spinner-border" role="status" />
              </div>
            ) : onboardingViewModal.data ? (
              <div>
                <div style={{ padding: "12px 14px", background: "#f4f5f7", borderRadius: 8, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{onboardingViewModal.data.run?.candidate_name || "—"}</div>
                  <div style={{ fontSize: 12, color: "#8a9199", marginTop: 2 }}>{onboardingViewModal.data.run?.email}</div>
                  <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "#525b66" }}>Flow: <strong>{onboardingViewModal.data.flow_name || "—"}</strong></span>
                    <span style={{ fontSize: 12, color: "#525b66" }}>Job: <strong>{onboardingViewModal.data.run?.job_title || "—"}</strong></span>
                    <span style={{ fontSize: 12, color: "#525b66" }}>Joining: <strong>{onboardingViewModal.data.run?.joining_date ? new Date(onboardingViewModal.data.run.joining_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}</strong></span>
                  </div>
                </div>
                {(() => {
                  const phases = onboardingViewModal.data.phases || [];
                  const pct = phases.length ? Math.round((phases.filter((p) => p.status === "completed").length / phases.length) * 100) : 0;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#525b66", marginBottom: 8 }}>Overall Progress</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ flex: 1, height: 8, borderRadius: 99, background: "#e4e9f0", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: "#f75891", borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#f75891" }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })()}
                <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: "#525b66" }}>Phases</div>
                {(onboardingViewModal.data.phases || []).map((s, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: s.status === "completed" ? "#f75891" : s.status === "in_progress" ? "#3b82f6" : "#e4e9f0", flexShrink: 0 }}>
                      {s.status === "completed" ? (
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      ) : (
                        <span style={{ fontSize: 11, color: s.status === "in_progress" ? "#fff" : "#8a9199", fontWeight: 600 }}>{idx + 1}</span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500, color: "#1a1a2e" }}>{s.label}</div></div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: s.status === "completed" ? "#dcfce7" : s.status === "in_progress" ? "#dbeafe" : "#f3f4f6", color: s.status === "completed" ? "#16a34a" : s.status === "in_progress" ? "#1d4ed8" : "#6b7280" }}>
                      {s.status === "completed" ? "Done" : s.status === "in_progress" ? "In Progress" : "Pending"}
                    </span>
                  </div>
                ))}
                {(onboardingViewModal.data.forms || []).length > 0 && (
                  <>
                    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, fontWeight: 600, color: "#525b66" }}>Forms</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {onboardingViewModal.data.forms.map((f, idx) => {
                        const isFormOpen = expandedOnboardingForms[f.form_id] !== false;
                        const responseEntries = Object.entries(f.responses || {});
                        return (
                          <div key={idx} style={{ border: "1px solid #e4e9f0", borderRadius: 10, overflow: "hidden" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", cursor: "pointer", background: isFormOpen ? "#fafbfc" : "#fff" }} onClick={() => toggleOnboardingForm(f.form_id)}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{f.form_name}</div>
                                {f.completed_at && <div style={{ fontSize: 11, color: "#8a9199", marginTop: 2 }}>Completed {new Date(f.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>}
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: f.status === "completed" ? "#dcfce7" : f.status === "in_progress" ? "#dbeafe" : "#f3f4f6", color: f.status === "completed" ? "#16a34a" : f.status === "in_progress" ? "#1d4ed8" : "#6b7280" }}>
                                {f.status === "completed" ? "Done" : f.status === "in_progress" ? "In Progress" : "Not Started"}
                              </span>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8a9199" strokeWidth="2" style={{ flexShrink: 0, transform: isFormOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9" /></svg>
                            </div>
                            {isFormOpen && (
                              <div style={{ background: "#f8f9fb", padding: "10px 14px 12px", borderTop: "1px solid #eef0f3" }}>
                                {responseEntries.length === 0 ? (
                                  <div style={{ fontSize: 12, color: "#8a9199", fontStyle: "italic" }}>No responses yet.</div>
                                ) : responseEntries.map(([label, value]) => {
                                  const isFile = value && typeof value === "object" && value.file_url;
                                  return (
                                    <div key={label} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid #eef0f3", flexWrap: "wrap", alignItems: "center" }}>
                                      <span style={{ fontSize: 12, color: "#8a9199", minWidth: 140, flexShrink: 0 }}>{label}</span>
                                      {isFile ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                          <span style={{ fontSize: 12, color: "#1a1a2e", fontWeight: 500 }}>{value.file_name}</span>
                                          <a href={value.file_url} download={value.file_name} target="_blank" rel="noreferrer" style={{ color: "#f75891" }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                          </a>
                                        </div>
                                      ) : (
                                        <span style={{ fontSize: 12, color: "#1a1a2e", fontWeight: 500, wordBreak: "break-all" }}>{String(value)}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {(onboardingViewModal.data.documents || []).length > 0 && (
                  <>
                    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, fontWeight: 600, color: "#525b66" }}>Documents</div>
                    {onboardingViewModal.data.documents.map((d, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500, color: "#1a1a2e" }}>{d.name}</div></div>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, textTransform: "capitalize", background: ["completed", "accepted"].includes(d.status) ? "#dcfce7" : d.status === "pending" ? "#fef3c7" : d.status === "rejected" ? "#fee2e2" : "#f3f4f6", color: ["completed", "accepted"].includes(d.status) ? "#16a34a" : d.status === "pending" ? "#d97706" : d.status === "rejected" ? "#dc2626" : "#6b7280" }}>
                          {d.status}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: 20, color: "#8a9199", fontSize: 13 }}>No data available</div>
            )}
            {!onboardingViewModal.loading && onboardingViewModal.data?.run?.status === "in_review" && canEditOnboarding && (
              <div style={{ display: "flex", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #e4e9f0" }}>
                <button style={{ flex: 1, padding: "9px 18px", borderRadius: 8, border: "1px solid #e4e9f0", background: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#525b66" }} onClick={() => handleOnboardingRequestChanges(onboardingViewModal.runId)}>Request Changes</button>
                <button style={{ flex: 1, padding: "9px 18px", borderRadius: 8, border: "1px solid #22c55e", background: "#22c55e", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#fff" }} onClick={() => handleCompleteOnboardingReview(onboardingViewModal.runId)}>Mark Completed</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Hiring status change modal (mandatory comment) ───────── */}
      {statusChangeModal.open && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(26,26,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 16 }}
          onClick={() => setStatusChangeModal({ open: false, status: null, comment: "" })}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 24, width: "100%", maxWidth: 420, boxShadow: "0 8px 40px rgba(26,26,46,0.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1a1a2e" }}>Change hiring status</h3>
              <img src="/images/close-icon.svg" alt="Close" style={{ cursor: "pointer", width: 18, height: 18 }} onClick={() => setStatusChangeModal({ open: false, status: null, comment: "" })} />
            </div>
            <p style={{ fontSize: 13, color: "#525b66", margin: "0 0 14px" }}>
              You're moving this candidate from <b>{hiringStatusName}</b> to <b>{statusChangeModal.status?.name}</b>. Please add a quick note explaining the change — it will be saved as a note.
            </p>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#525b66", display: "block", marginBottom: 6 }}>Comment <span style={{ color: "#f75891" }}>*</span></label>
            <textarea
              autoFocus rows={4}
              style={{ width: "100%", padding: "9px 12px", border: "1px solid #e4e9f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }}
              placeholder="Add a comment explaining this change"
              value={statusChangeModal.comment}
              onChange={(e) => setStatusChangeModal((m) => ({ ...m, comment: e.target.value }))}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e4e9f0", background: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#525b66", minWidth: 80 }} onClick={() => setStatusChangeModal({ open: false, status: null, comment: "" })}>Cancel</button>
              <button
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #f75891", background: "#f75891", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", color: "#fff", minWidth: 80, opacity: (!statusChangeModal.comment.trim() || statusLoading) ? 0.5 : 1 }}
                disabled={!statusChangeModal.comment.trim() || statusLoading}
                onClick={handleConfirmStatusChange}
              >
                {statusLoading ? "Updating…" : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ApplyStageModal ───────────────────────────────────────── */}
      <ApplyStageModal
        isOpen={isOpen}
        setIsOpen={() => setIsOpen(false)}
        selectAssignedStage={selectAssignedStage}
        jobId={jobId}
        candidateId={candidateId}
        loading={stageLoading}
        assignProgress={assignProgress}
        preselectStage={preselectStage}
        stagePermissions={stagePermissionMap}
      />
    </div>
  );
};

export default CandidateProfileV2;
